import React from 'react';
import {
  Box,
  Button,
  Divider,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../stores/store';
import { setCategoryId, setTypeId } from '../../../stores/selectIds/slice';
import { fetchTypeLabels } from '../../../stores/TypeLabels/slice';
import { isGetTypeLabelList } from '../../../_utils/typeGuardUtils';
import { INSPEX_PATHNAME } from '../../../_constants/Const';
import { isRejected } from '@reduxjs/toolkit';
import ErrorModal from '../../../_common/Modal/ErrorModal';
import useErrorModal from '../../../hooks/useErrorModal';
import { API_CODE } from '../../../_constants/apiConst';

type CategoryCardProps = {
  rmaNo: string;
  isInspection: boolean;
  id: string;
  label: string;
  progress: number;
  pathIcon: string;
};

const CategoryCard: React.FC<CategoryCardProps> = ({
  rmaNo,
  isInspection,
  id,
  label,
  progress,
  pathIcon,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const { categoryId } = useSelector((state: RootState) => state.selectIds);
  const { typeLabelsMap } = useSelector((state: RootState) => state.typeLabels);
  const { main } = theme.palette.primary;
  const { error, showApiError, showTypeError, hideError } = useErrorModal();

  const handleCardClick = async () => {
    // 中断時の大項目と異なる場合
    if (id !== categoryId) {
      let newTypeId = '';
      if (!typeLabelsMap[id] || typeLabelsMap[id].length === 0) {
        const response = await dispatch(
          fetchTypeLabels({ query: { rmaNo, categoryId: id }, isInspection }),
        );
        const apiCode = isInspection
          ? API_CODE.INSPECTION_TYPE_LABELS
          : API_CODE.QC_TYPE_LABELS;
        if (isRejected(response)) {
          showApiError(response.payload, apiCode);
          return;
        }
        if (isGetTypeLabelList(response.payload)) {
          newTypeId = response.payload[0].id;
        } else {
          showTypeError(apiCode);
          return;
        }
      } else {
        newTypeId = typeLabelsMap[id][0].id;
      }
      dispatch(setTypeId(newTypeId));
    }
    navigate(INSPEX_PATHNAME.INSPECTION + id);
    dispatch(setCategoryId(id));
  };

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        width: {
          xs: 'calc(50% - 16px)',
          md: 'calc(33% - 16px)',
          lg: 'calc(25% - 16px)',
        },
      }}
    >
      <Button
        variant={progress === 100 ? 'contained' : 'outlined'}
        fullWidth
        component={Link}
        onClick={handleCardClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '16px',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 50,
            backgroundColor: progress === 100 ? '#FFFFFF' : main,
            maskImage: `url(${pathIcon})`,
            maskRepeat: 'no-repeat',
            maskSize: 'contain',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            height: '60px',
            alignItems: 'center',
            alignContent: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: progress === 100 ? '#FFFFFF' : main,
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
          }}
        >
          {label}
        </Typography>
        <Divider
          variant="middle"
          sx={{
            width: '100%',
            marginTop: '5px',
            marginBottom: '10px',
            borderColor: progress === 100 ? '#FFFFFF' : main,
          }}
        />
        <Typography variant="h4" color={progress === 100 ? '#FFFFFF' : main}>
          {progress}

          <Box
            sx={{
              fontSize: '16px',
              display: 'inline',
              color: progress === 100 ? '#FFFFFF' : main,
            }}
          >
            %
          </Box>
        </Typography>
      </Button>
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default CategoryCard;
